import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import PageSection from 'components/page-section';
import { useMobileMenuContext } from 'contexts';
import { Header, KidsCoursesContent } from './components';
import { useKidsCourses } from 'contexts/kids-course.context';
import { KidsCourseAgeGroup, KidsCourseTag } from 'types';
import { FullScreenLoader } from 'components';
import { langMapper } from 'utils';
import { useTranslation } from 'react-i18next';

const sectionConfig = {
    CWrap: {
        css: {
            position: 'relative',
            paddingTop: '128px',
            paddingBottom: '170px',
            marginTop: '-64px',
            overflow: 'hidden',

            '@media(max-width: 1279px)': {
                paddingTop: '100px',
            },

            '@media(max-width: 1023px)': {
                paddingTop: '74px',
            },
            '@media(max-width: 768px)': {
                paddingBottom: '200px',
            },
        },
    },
    ContentArea: {
        css: {
            maxWidth: '1240px',
            width: '100%',
            display: 'block',
        },
    },
};

const KidsCourses = () => {
    const { toggleKidsCoursesStyles } = useMobileMenuContext();
    const { i18n } = useTranslation('pages/kids-courses');

    useEffect(() => {
        toggleKidsCoursesStyles(true);
    }, []);

    const {
        projects,
        materials,
        hasMoreProjects,
        hasMoreMaterials,
        isProjectsLoading,
        isMaterialsLoading,
        fetchItems,
        projectsTotal,
        materialsTotal,
        settings,
        fetchSettings,
    } = useKidsCourses();

    const { toggleProfessionStyles } = useMobileMenuContext();

    const [searchParams, setSearchParams] = useSearchParams();

    const getAgesFromUrl = (): KidsCourseAgeGroup[] => {
        const param = searchParams.get('ages');
        return param ? param.split(',').map(Number) : [];
    };

    const getInterestsFromUrl = () => {
        const param = searchParams.get('interests');
        if (!param) return { projects: false, materials: false };

        const interestsList = param.split(',');
        return {
            projects: interestsList.includes('projects'),
            materials: interestsList.includes('materials'),
        };
    };

    const [selectedAges, setSelectedAges] =
        useState<KidsCourseAgeGroup[]>(getAgesFromUrl);
    const [interests, setInterests] = useState(getInterestsFromUrl);

    const isNothingSelected = !interests.projects && !interests.materials;

    const shouldShowProjects = isNothingSelected || interests.projects;
    const shouldShowMaterials = isNothingSelected || interests.materials;

    useEffect(() => {
        const params = new URLSearchParams();

        if (selectedAges.length > 0) {
            params.set('ages', selectedAges.join(','));
        }

        const activeInterests = [];
        if (interests.projects) activeInterests.push('projects');
        if (interests.materials) activeInterests.push('materials');

        if (activeInterests.length > 0) {
            params.set('interests', activeInterests.join(','));
        }

        setSearchParams(params);
    }, [selectedAges, interests, setSearchParams]);

    useEffect(() => {
        if (shouldShowProjects) {
            fetchItems(
                KidsCourseTag.Projects,
                false,
                langMapper[i18n.language],
                selectedAges
            );
        }
    }, [shouldShowProjects, selectedAges, fetchItems]);

    useEffect(() => {
        if (shouldShowMaterials) {
            fetchItems(
                KidsCourseTag.LearningMaterials,
                false,
                langMapper[i18n.language],
                selectedAges
            );
        }
    }, [shouldShowMaterials, selectedAges, fetchItems]);

    useEffect(() => {
        fetchSettings(langMapper[i18n.language]);
    }, []);

    const handleAgeToggle = (age: KidsCourseAgeGroup) => {
        setSelectedAges((prev) =>
            prev.includes(age) ? prev.filter((a) => a !== age) : [...prev, age]
        );
    };

    const handleInterestToggle = (type: 'projects' | 'materials') => {
        setInterests((prev) => ({ ...prev, [type]: !prev[type] }));
    };

    const handleLoadMoreProjects = () => {
        fetchItems(
            KidsCourseTag.Projects,
            true,
            langMapper[i18n.language],
            selectedAges
        );
    };

    const handleLoadMoreMaterials = () => {
        fetchItems(
            KidsCourseTag.LearningMaterials,
            true,
            langMapper[i18n.language],
            selectedAges
        );
    };

    useEffect(() => {
        toggleProfessionStyles(true);

        return () => {
            toggleProfessionStyles(false);
        };
    }, []);

    if (!settings) {
        return <FullScreenLoader />;
    }

    return (
        <>
            <PageSection
                config={{
                    ...sectionConfig,
                    ContentArea: {
                        ...sectionConfig.ContentArea,
                        css: {
                            ...sectionConfig.ContentArea.css,
                            backgroundImage: 'none',
                        },
                    },
                }}>
                <Header settings={settings} />
            </PageSection>

            <KidsCoursesContent
                projects={projects}
                materials={materials}
                hasMoreProjects={hasMoreProjects}
                hasMoreMaterials={hasMoreMaterials}
                isProjectsLoading={isProjectsLoading}
                isMaterialsLoading={isMaterialsLoading}
                checkedInterests={interests}
                selectedAges={selectedAges}
                shouldShowProjects={shouldShowProjects}
                shouldShowMaterials={shouldShowMaterials}
                onAgeToggle={handleAgeToggle}
                onInterestToggle={handleInterestToggle}
                onLoadMoreProjects={handleLoadMoreProjects}
                onLoadMoreMaterials={handleLoadMoreMaterials}
                projectsTotal={projectsTotal}
                materialsTotal={materialsTotal}
            />
        </>
    );
};

export default KidsCourses;
